import 'twin.macro';

import { Button, ButtonGroup, Heading, Modal, Stack } from '@cheqroom/ui';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomField, SystemField, SystemFieldType as ItemSystemFieldType } from '../../hooks/item-fields/types';
import { EXCLUDE_ITEM_FIELDS } from '../../hooks/item-fields/use-item-fields-config';
import { EXCLUDE_KIT_FIELDS } from '../../hooks/kit-fields/use-kit-fields-config';
import { ModalProps } from '../../hooks/modal/use-modal';
import CustomizeColumns from './CustomizeColumns';
import CustomizeItemView from './CustomizeItemView';

export type Props = {
	type:
		| 'items-list'
		| 'check-outs-list'
		| 'contacts-list'
		| 'kits-list'
		| 'reservations-list'
		| 'spotchecks-list'
		| 'tickets-list';
	viewType: 'table' | 'cards' | 'scheduler';
	systemFields: SystemField<string>[];
	customFields: CustomField[];
	selectedColumns?: string[];
	saveConfig: (selectedColumns: string[], selectedFields?: string[]) => void;
	selectedFields?: string[];
};

const EXCLUDE_FIELDS = {
	'items-list': EXCLUDE_ITEM_FIELDS,
	'kits-list': EXCLUDE_KIT_FIELDS,
	'reservations-list': [],
	'spotchecks-list': [],
	'check-outs-list': [],
	'contacts-list': [],
	'tickets-list': [],
};

const CustomizeOverviewModal: FC<ModalProps & Props> = ({
	type,
	viewType,
	onClose,
	systemFields,
	customFields,
	selectedColumns,
	saveConfig,
	selectedFields = [],
}) => {
	const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>(selectedColumns ?? []);
	const { t } = useTranslation('customize-overview-columns');

	const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>(
		selectedFields.filter((field) => !EXCLUDE_FIELDS[type].includes(field))
	);

	const handleSaveChangesClick = () => {
		if (['items-list', 'kits-list'].includes(type)) {
			saveConfig(selectedColumnIds, selectedFieldIds);
			window.app.trigger('items:changed');
		} else {
			saveConfig(selectedColumnIds);
		}

		onClose();
	};

	// Exclude fields from view system fields
	const customizeItemViewSystemFields = systemFields.filter((field) => !EXCLUDE_FIELDS[type].includes(field.id));

	return (
		<Modal
			size="full-page"
			open
			header={
				<Modal.Header onClose={onClose}>
					<Heading element="h1">{t(`title.${type}`)}</Heading>
				</Modal.Header>
			}
			footer={
				<Modal.Footer sticky>
					<ButtonGroup>
						<Button variation="secondary" onClick={onClose}>
							{t('actions.cancel')}
						</Button>
						<Button variation="primary" onClick={handleSaveChangesClick}>
							{t('actions.save')}
						</Button>
					</ButtonGroup>
				</Modal.Footer>
			}
		>
			<Modal.Body>
				<Stack vertical spacing="loose">
					{viewType === 'table' && (
						<CustomizeColumns
							type={type}
							systemFields={systemFields}
							customFields={customFields}
							selectedColumnIds={selectedColumnIds}
							setSelectedColumnIds={setSelectedColumnIds}
						/>
					)}
					{['items-list', 'kits-list'].includes(type) && (
						<CustomizeItemView
							type={type as 'items-list' | 'kits-list'}
							viewType={viewType}
							systemFields={customizeItemViewSystemFields as SystemField<ItemSystemFieldType>[]}
							customFields={customFields}
							selectedFieldIds={selectedFieldIds}
							setSelectedFieldIds={setSelectedFieldIds}
						/>
					)}
				</Stack>
			</Modal.Body>
		</Modal>
	);
};

export default CustomizeOverviewModal;
